/**
 * A `fetch` with a ceiling on how long it may stay outstanding, plus the two
 * transport properties every tRPC call needs: the session cookie, and no cache.
 *
 * **A hung radio does not fail — it holds.** tRPC forwards only the operation's
 * own signal, so without a deadline of ours a phone whose modem is attached but
 * useless leaves the request open with nothing to reject, and the driver's
 * confirm button reads "Saving…" for all of it.
 *
 * **The composition is hand-rolled rather than `AbortSignal.any`.** That static
 * needs Safari 17.4, and the iOS shell's deployment target is 16.0 while
 * pointing at the live site — so on an installed app running iOS 16 it is
 * `undefined`, and since `httpBatchLink` supplies a signal on *every* request,
 * the throw would take out every call on every surface rather than degrading.
 * `@trpc/client` hand-rolls the same helper for the same reason and calls it
 * "basically, a ponyfill". Ours additionally forwards the abort REASON, which
 * theirs drops, so a timeout is still distinguishable from a cancellation.
 */
export function fetchWithTimeout(timeoutMs: number) {
  return async (url: URL | RequestInfo, options: RequestInit | undefined) => {
    const controller = new AbortController()
    const abort = (reason: unknown) => controller.abort(reason)

    const caller = options?.signal
    if (caller?.aborted) abort(caller.reason)
    else
      caller?.addEventListener('abort', () => abort(caller.reason), {
        once: true,
      })

    // `DOMException` so the rejection is indistinguishable from the platform's
    // own timeout — `isNoServerResponse` reads `TRPCClientError.data`, but the
    // name is what a human sees in a log.
    setTimeout(
      () => abort(new DOMException('The operation timed out.', 'TimeoutError')),
      timeoutMs,
    )

    // Deliberately NOT cleared when `fetch` resolves. That happens when the
    // response HEADERS arrive, and tRPC reads the body afterwards
    // (`fetchHTTPResponse` then `res.json()`), so clearing there leaves a
    // connection that dies mid-body with no ceiling at all — measured against a
    // server that flushes headers and then stops: cleared at the resolve, the
    // request outlived its ceiling until something else killed the socket;
    // kept live, it rejected on time. That case IS the hung radio this exists
    // for.
    //
    // The cost is a timer that lives out the ceiling even for a request
    // answered in 200ms — so the live count is one per request issued within
    // the last ceiling, not one per request in flight. Measured, a pending
    // timer retains only the controller, the caller's signal and this closure;
    // the request body is collected. The cheaper of the two errors.
    return await fetch(url, {
      ...options,
      credentials: 'include',
      cache: 'no-store',
      signal: controller.signal,
    })
  }
}
